/** Type guard to check if a node is a `ShadowRoot` */
export const isShadowRoot = (el: Node): el is ShadowRoot => el instanceof ShadowRoot;

/** Checks if an element is contained within a shadow DOM tree.
 * Returns true if the element's root node is a `ShadowRoot`. */
export const isShadowElement = (el: Node) => isShadowRoot(el.getRootNode());

/** Check if element is a custom element with an attached shadow root.
 * Custom elements are identified by having a hyphen in their tag name. */
export const isCustomElementWithShadowRoot = (el: Element): el is HTMLElement & { shadowRoot: ShadowRoot } => Boolean(el.tagName.includes("-") && el.shadowRoot);

/** Generates all ancestor elements of the given element, traversing
 * across shadow DOM boundaries. Yields the element itself first, then
 * walks up through parents and shadow hosts. */
export const shadowPiercingAncestors = function* (element: Node) {
  yield element;
  let current: Node = element;
  let parent: Node | null;

  while ((parent = getShadowPiercingParent(current)) && parent?.nodeType === Node.ELEMENT_NODE) {
    yield parent;
    current = parent;
  }
};

/** Gets the parent of a node, crossing shadow DOM boundaries when
 * necessary. Returns the regular `parentNode` if available, otherwise
 * returns the shadow host if the node is at a shadow root boundary. */
const getShadowPiercingParent = (node: Node): Node | null => {
  const parent = node.parentNode;

  if (!parent) return null;
  if (isShadowRoot(parent)) return parent.host;
  else return parent;
};

/** Checks if a container element contains another element, traversing
 * across shadow DOM boundaries. Unlike the native contains() method,
 * this function will pierce through shadow roots to find containment
 * relationships that span multiple DOM trees. */
export const shadowPiercingContains = (container: HTMLElement | Document, el: HTMLElement): boolean => {
  let current: Node | null = el;

  /** Early light DOM check. Light DOM contains is
   * definitive if element is not a shadow element. */
  if (container.contains(el)) return true;
  if (!isShadowElement(el)) return false;

  const containerShadowRoot = "shadowRoot" in container ? container.shadowRoot : null;
  if (containerShadowRoot?.contains(el)) return true;

  while (current) {
    /** If it's a shadow root, check if container contains its host
     * or that the container's shadow root contains the host */
    const rootNode: Node = current.getRootNode();

    /** Not a shadow root: stop traversing */
    if (rootNode === document) return false;
    if (!isShadowRoot(rootNode)) return false;

    const host = rootNode.host;

    /** Reached the container itself */
    if (host === container) return true;

    /** Check BOTH light DOM and shadow DOM for the host */
    if (container.contains(host)) return true;
    if (containerShadowRoot?.contains(host)) return true;

    current = host;
  }

  return false;
};

/** Queries for an element using a CSS selector, searching both light DOM
 * and the immediate shadow DOM (depth 1 only). For Document nodes, performs
 * a standard querySelector. For HTMLElements, searches the light DOM first,
 * then falls back to the element's direct shadow root if no match is found.
 * Does not traverse nested shadow DOMs. */
export const shallowShadowQuerySelector = (el: HTMLElement | Document, selector: string): HTMLElement | null => {
  if (!(el instanceof HTMLElement)) return el.querySelector<HTMLElement>(selector);
  return el.querySelector<HTMLElement>(selector) ?? el.shadowRoot?.querySelector<HTMLElement>(selector) ?? null;
};
